/**
 * The client's Главная: is the bot trading, the button that changes that, and
 * the money in four figures (owner, 2026-10-15).
 *
 * Plain words only. No Score, versions, pools or contract digests: the
 * platform runs the strategy, and a client who reads "allocation" or "epoch"
 * learns nothing about whether the money is working. Every state is the
 * server's; this page names it.
 */
import { Link } from "react-router";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { STEPS } from "@/shared/onboarding/Steps";

import { useOnboarding } from "./StartPage";
import { StatusBadge } from "@/design-system/StatusBadge";

import { useClientAccount, usePauseTrading } from "../identity/AccountPanel";
import { ClientReturns } from "./ClientReturns";
import { StartControl, startStep, useTradingControl } from "../identity/StartControl";

function TradingStatus({ tenantId, accountId, onChanged }: {
  readonly tenantId: string; readonly accountId: string; readonly onChanged: () => Promise<void>;
}): React.JSX.Element {
  const control = useTradingControl(tenantId, accountId);
  const pause = usePauseTrading(tenantId, accountId, onChanged);
  const data = control.data;
  if (data === undefined) {
    return (
      <Panel title="Торговля" testId="client-status">
        <p role="status" className="text-body-main text-text-secondary">
          {control.isError ? "Состояние торговли сейчас недоступно. Обновите страницу." : "Проверяю состояние…"}
        </p>
      </Panel>
    );
  }
  const running = data.admissions_enabled;
  const step = startStep(data, false);
  // A failed re-read keeps the last answer; it is said to be the last one.
  const unheard = control.isRefetchError;
  return (
    <Panel
      title="Торговля"
      testId="client-status"
      trailing={<StatusBadge tone={running ? "ready" : step.tone}>{running ? "Бот торгует" : step.state}</StatusBadge>}
    >
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        <p>
          {running
            ? "Новые сделки открываются автоматически. Открытые сделки бот ведёт до закрытия."
            : "Новые сделки не открываются. Открытые сделки бот всё равно ведёт до закрытия."}
        </p>
        {running && data.can_manage && (
          <div className="flex flex-col gap-2">
            <div>
              <Button onClick={() => { pause.mutate(); }} disabled={pause.isPending}>Остановить</Button>
            </div>
            <p className="text-helper-sm">Остановка не закрывает открытые сделки и не трогает деньги на бирже.</p>
          </div>
        )}
        {!running && <StartControl tenantId={tenantId} accountId={accountId} control={data} onChanged={onChanged} />}
        {pause.isError && <p role="alert" className="text-status-warning">Остановка не подтверждена. Обновите страницу и попробуйте ещё раз.</p>}
        {unheard && <p role="status" className="text-helper-sm text-status-warning" data-testid="client-status-stale">Состояние не обновляется — показано последнее известное.</p>}
      </div>
    </Panel>
  );
}

/** Until the bot runs, the way to finish connecting is the first thing seen. */
function OnboardingBanner({ tenantId, accountId }: { readonly tenantId: string; readonly accountId: string }): React.JSX.Element | null {
  const { step } = useOnboarding(tenantId, accountId);
  if (step === null || step >= 5) return null;
  return (
    <Panel title="Завершите подключение" testId="onboarding-banner">
      <div className="flex flex-wrap items-center justify-between gap-3 text-body-main text-text-secondary">
        <span>Шаг {String(step + 1)} из 5: {STEPS[step]}.</span>
        <Link className="cursor-pointer rounded-row bg-action-primary px-4 py-2 font-medium text-text-on-action transition-colors duration-150 hover:bg-action-hover focus-visible:outline-2 focus-visible:outline-focus-ring" to="/app/start">Продолжить</Link>
      </div>
    </Panel>
  );
}

export function ClientHome({ tenantId, accountId, children }: {
  readonly tenantId: string;
  readonly accountId: string;
  /** The account's overview, the same the owner reads (owner, 2026-10-31). */
  readonly children?: React.ReactNode;
}): React.JSX.Element {
  const account = useClientAccount(tenantId, accountId);
  const refresh = async (): Promise<void> => { await account.refetch(); };

  return (
    <div className="flex flex-col gap-3" data-testid="client-home">
      <OnboardingBanner tenantId={tenantId} accountId={accountId} />
      <TradingStatus tenantId={tenantId} accountId={accountId} onChanged={refresh} />
      {children}
      <ClientReturns />
      <p className="text-body-main text-text-secondary">
        <Link className="cursor-pointer text-text-primary underline underline-offset-2 hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-focus-ring" to="/app/slots">Все сделки</Link>
        {" · "}
        <Link className="cursor-pointer text-text-primary underline underline-offset-2 hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-focus-ring" to="/app/billing">Пополнения и комиссия</Link>
        {" · "}
        <Link className="cursor-pointer text-text-primary underline underline-offset-2 hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-focus-ring" to="/app/help">Помощь</Link>
      </p>
    </div>
  );
}
